import type { SanitizedEmail } from './emailHtml';

export interface ParsedEmailContent {
  mainContent: { styles: string; body: string };
  quotedContent: { styles: string; body: string } | null;
  hasQuotedContent: boolean;
  /** True when everything in the message is quoted (e.g. a reply sent with no
   *  new text) — callers should default the quoted section to expanded so the
   *  message doesn't render blank. */
  mainIsEmpty: boolean;
}

/**
 * Quote detection is a BOUNDARY problem, not a containment problem. Gmail and
 * Apple Mail wrap quoted history in an element, but Outlook (the most common
 * business client) inserts a divider div that wraps ONLY the From/Sent/To/
 * Subject header line — the quoted body follows as SIBLINGS at the same DOM
 * level, so no element selector can ever capture it. The only semantic that
 * works across clients is: find the FIRST quote marker in document order and
 * treat that node plus everything after it as quoted.
 */

// Markers that are unambiguous reply/forward containers or dividers. These
// qualify unconditionally — they never appear as decorative content.
const EXPLICIT_MARKER_SELECTOR = [
  // Our own compose format (materialized by the sanitizer for legacy rows)
  'div.quoted-content-block',
  'div[data-quoted-content]',
  // Gmail
  '.gmail_quote_container',
  '.gmail_quote',
  // Outlook web / new Outlook / Outlook mobile
  '#divRplyFwdMsg',
  '#appendonsend',
  '#mail-editor-reference-message-container',
  '.OutlookMessageHeader',
  // Thunderbird / Yahoo
  '.moz-cite-prefix',
  '.yahoo_quoted',
].join(', ');

// Outlook desktop (Word-generated) divider: a div styled with a border-top
// rule containing the bolded From:/Sent:/To:/Subject: header. The bold-label
// check is locale-independent (Word bolds each label in any language).
function isOutlookDivider(el: Element): boolean {
  const style = el.getAttribute('style') || '';
  if (!/border-top:\s*[^;]*solid/i.test(style)) return false;
  const text = (el.textContent || '').slice(0, 500);
  const labelMatches = text.match(/\b(From|Sent|To|Cc|Subject|Date)\s*:/gi);
  if (labelMatches && labelMatches.length >= 2) return true;
  let boldLabels = 0;
  el.querySelectorAll('b, strong').forEach((b) => {
    if ((b.textContent || '').trim().endsWith(':')) boldLabels++;
  });
  return boldLabels >= 2;
}

// Plain-text-style dividers that survive as paragraphs: OWA's underscore rule
// and the classic "-----Original Message-----" line.
function isTextDivider(el: Element): boolean {
  if (el.children.length > 3) return false;
  const text = (el.textContent || '').trim();
  if (!text || text.length > 80) return false;
  return /^_{15,}$/.test(text) || /^-{2,}\s*(Original|Forwarded)\s+Message\s*-{2,}$/i.test(text);
}

const ATTRIBUTION_RE = /\bwrote:\s*$|\bschrieb\b[\s\S]{0,100}:$|\ba écrit\s*:$|\bescribió\s*:$|^-{2,}\s*(Original|Forwarded)\s+Message\s*-{2,}$/i;

// A bare <blockquote> is only a quote if something signals it: an Apple Mail
// type="cite", a preceding attribution line, or reply headers inside it.
// Spam newsletters wrap random filler text in trailing blockquotes to game
// filters — without a signal those must not sprout a "Show quoted text"
// button.
function looksLikeQuote(bq: Element): boolean {
  if ((bq.getAttribute('type') || '').toLowerCase() === 'cite') return true;
  let prev = bq.previousElementSibling;
  while (prev && !(prev.textContent || '').trim()) {
    prev = prev.previousElementSibling;
  }
  if (prev) {
    const prevText = (prev.textContent || '').trim();
    if (prevText.length < 400 && ATTRIBUTION_RE.test(prevText)) return true;
  }
  const head = (bq.textContent || '').slice(0, 300);
  return /\b(From|Sent|Subject)\s*:/.test(head) || /\bwrote:/.test(head);
}

function hasBlockquoteAncestor(el: Element, root: Element): boolean {
  let cur = el.parentElement;
  while (cur && cur !== root) {
    if (cur.tagName === 'BLOCKQUOTE') return true;
    cur = cur.parentElement;
  }
  return false;
}

// Meaningful text after `node` in document order, excluding whitespace. Used
// to keep decorative mid-content blockquotes (newsletters) from qualifying:
// a real trailing quote has essentially nothing after it.
function textLengthAfter(doc: Document, node: Element): number {
  const range = doc.createRange();
  range.selectNodeContents(doc.body);
  range.setStartAfter(node);
  return range.toString().trim().length;
}

function findQuoteBoundary(doc: Document): Element | null {
  const body = doc.body;
  if (!body) return null;

  const candidates: Element[] = [];

  body.querySelectorAll(EXPLICIT_MARKER_SELECTOR).forEach((el) => {
    if (!hasBlockquoteAncestor(el, body)) candidates.push(el);
  });

  body.querySelectorAll('div[style*="border-top"]').forEach((el) => {
    if (!hasBlockquoteAncestor(el, body) && isOutlookDivider(el)) candidates.push(el);
  });

  body.querySelectorAll('p, div').forEach((el) => {
    if (isTextDivider(el) && !hasBlockquoteAncestor(el, body)) candidates.push(el);
  });

  // Generic blockquotes qualify only when the content after them is
  // negligible — a trailing quote, not a decorative pull-quote mid-message —
  // AND they carry an actual quote signal (type=cite, attribution line,
  // embedded reply headers). The trailing guard also protects interleaved
  // (bottom-posted) replies, where collapsing from the first quote would
  // hide the answers between quotes.
  body.querySelectorAll('blockquote').forEach((el) => {
    if (hasBlockquoteAncestor(el, body)) return;
    if (!looksLikeQuote(el)) return;
    if (textLengthAfter(doc, el) < 200) candidates.push(el);
  });

  if (candidates.length === 0) return null;

  // Earliest candidate in document order wins.
  let earliest = candidates[0];
  for (const candidate of candidates.slice(1)) {
    const pos = earliest.compareDocumentPosition(candidate);
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) earliest = candidate;
  }

  // Extend the boundary backward over attribution lines ("On … wrote:",
  // "-----Original Message-----") that immediately precede the marker —
  // they belong to the quote, not the new content.
  let boundary = earliest;
  for (let hops = 0; hops < 2; hops++) {
    let prev = boundary.previousElementSibling;
    while (prev && !(prev.textContent || '').trim() && prev.tagName !== 'IMG') {
      prev = prev.previousElementSibling;
    }
    if (!prev) break;
    const text = (prev.textContent || '').trim();
    if (text.length > 0 && text.length < 400 && ATTRIBUTION_RE.test(text)) {
      boundary = prev;
    } else {
      break;
    }
  }

  return boundary;
}

export function splitQuotedContent(sanitized: SanitizedEmail): ParsedEmailContent {
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized.body, 'text/html');

  const boundary = findQuoteBoundary(doc);

  if (!boundary) {
    return {
      mainContent: sanitized,
      quotedContent: null,
      hasQuotedContent: false,
      mainIsEmpty: false,
    };
  }

  // Split the document at the boundary: the marker and EVERYTHING after it in
  // document order becomes the quoted section. Range.extractContents splits
  // shared ancestors for us, which is what handles Outlook's divider-plus-
  // siblings shape (and markers nested inside wrapper tables/divs).
  const range = doc.createRange();
  range.selectNodeContents(doc.body);
  range.setStartBefore(boundary);
  const fragment = range.extractContents();

  const quotedContainer = doc.createElement('div');
  quotedContainer.appendChild(fragment);

  const mainBody = doc.body.innerHTML;
  const mainText = (doc.body.textContent || '').trim();
  const mainHasVisual = Boolean(doc.body.querySelector('img, svg, table'));

  return {
    mainContent: { styles: sanitized.styles, body: mainBody },
    quotedContent: { styles: sanitized.styles, body: quotedContainer.innerHTML },
    hasQuotedContent: true,
    mainIsEmpty: !mainText && !mainHasVisual,
  };
}
